/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import {
  Drawer,
  Table,
  Tag,
  Typography,
  Flex,
  Avatar,
  Empty,
  Descriptions,
  Divider,
  Spin,
} from 'antd';
import {
  ArrowDown,
  ArrowUp,
  CheckCircle,
  Clock,
  XCircle,
} from 'lucide-react';
import { useLanguage } from 'hooks/Language/useLanguage';
import dayjs from 'dayjs';

const { Text, Title } = Typography;

interface Transaction {
  Amount: string;
  Currency: string;
  Date: string;
  TransactionID: string;
  Description: string;
  NewBalance: string;
  TransStatusText: string;
  OpsType: string;
  TransStatus: string;
  DepositorName?: string;
}

interface TransactionViewDrawerProps {
  open: boolean;
  onClose: () => void;
  transactions: Transaction[];
  loading: boolean;
  accountNumber: string;
  startDate?: string;
  endDate?: string;
}

const TransactionViewDrawer: React.FC<TransactionViewDrawerProps> = ({
  open,
  onClose,
  transactions,
  loading,
  accountNumber,
  startDate,
  endDate,
}) => {
  const { translate, formatDate } = useLanguage();

  const formatNumber = (value: number | string) => {
    const numValue =
      typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return '0';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(numValue);
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case '1':
        return {
          color: 'success',
          icon: <CheckCircle size={12} />,
          label: translate('Completed'),
        };
      case '0':
        return {
          color: 'processing',
          icon: <Clock size={12} />,
          label: translate('Pending'),
        };
      default:
        return {
          color: 'error',
          icon: <XCircle size={12} />,
          label: translate('Failed'),
        };
    }
  };

  const columns = [
    {
      title: '',
      key: 'type',
      width: 50,
      fixed: 'left' as const,
      render: (_: any, record: Transaction) => (
        <Avatar
          style={{
            backgroundColor:
              record?.OpsType === '+' ? '#52c41a' : '#ff4d4f',
          }}
          size={28}
          icon={
            record?.OpsType === '+' ? (
              <ArrowDown size={14} />
            ) : (
              <ArrowUp size={14} />
            )
          }
        />
      ),
    },
    {
      title: translate('Date'),
      key: 'Date',
      dataIndex: 'Date',
      width: 140,
      render: (date: string) => <Text>{formatDate(date)}</Text>,
    },
    {
      title: translate('Transaction ID'),
      key: 'TransactionID',
      dataIndex: 'TransactionID',
      width: 150,
      render: (id: string) => (
        <Text
          copyable
          style={{ fontFamily: 'monospace', fontSize: 12 }}
        >
          {id}
        </Text>
      ),
    },
    {
      title: translate('Description'),
      key: 'Description',
      dataIndex: 'Description',
      ellipsis: true,
      render: (description: string) => (
        <Text ellipsis={{ tooltip: description }}>{description}</Text>
      ),
    },
    {
      title: translate('Debit'),
      key: 'debit',
      width: 140,
      align: 'right' as const,
      render: (_: any, record: Transaction) =>
        record.OpsType === '-' ? (
          <Text strong style={{ color: '#ff4d4f' }}>
            {record.Currency} {formatNumber(record.Amount)}
          </Text>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: translate('Credit'),
      key: 'credit',
      width: 140,
      align: 'right' as const,
      render: (_: any, record: Transaction) =>
        record.OpsType === '+' ? (
          <Text strong style={{ color: '#52c41a' }}>
            {record.Currency} {formatNumber(record.Amount)}
          </Text>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: translate('Balance'),
      key: 'NewBalance',
      width: 150,
      align: 'right' as const,
      render: (_: any, record: Transaction) => (
        <Text strong>
          {record.Currency} {formatNumber(record.NewBalance)}
        </Text>
      ),
    },
    {
      title: translate('Status'),
      key: 'status',
      width: 120,
      align: 'center' as const,
      render: (_: any, record: Transaction) => {
        const config = getStatusConfig(record.TransStatus);
        return (
          <Tag icon={config.icon} color={config.color}>
            {record.TransStatusText || config.label}
          </Tag>
        );
      },
    },
  ];

  const totalDebit = transactions
    .filter((t) => t.OpsType === '-')
    .reduce((sum, t) => sum + parseFloat(t.Amount || '0'), 0);

  const totalCredit = transactions
    .filter((t) => t.OpsType === '+')
    .reduce((sum, t) => sum + parseFloat(t.Amount || '0'), 0);

  const currency = transactions[0]?.Currency || 'XAF';

  return (
    <Drawer
      title={
        <Flex vertical gap={4}>
          <Title level={4} style={{ margin: 0 }}>
            {translate('Account Statement')}
          </Title>
          <Text type="secondary" style={{ fontWeight: 'normal' }}>
            {translate('Transaction history for the selected period')}
          </Text>
        </Flex>
      }
      placement="right"
      width={1000}
      onClose={onClose}
      open={open}
      styles={{
        body: { padding: '16px 24px' },
      }}
    >
      <Spin spinning={loading}>
        <Flex vertical gap="large">
          <Descriptions
            bordered
            size="small"
            column={{ xs: 1, sm: 2, md: 4 }}
          >
            <Descriptions.Item label={translate('Account Number')}>
              <Text strong copyable>
                {accountNumber}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label={translate('Period')}>
              <Text>
                {startDate && dayjs(startDate).format('DD MMM YYYY')}{' '}
                - {endDate && dayjs(endDate).format('DD MMM YYYY')}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item
              label={translate('Total Transactions')}
            >
              <Text strong>{transactions.length}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={translate('Currency')}>
              <Tag color="blue">{currency}</Tag>
            </Descriptions.Item>
          </Descriptions>

          <Flex gap="middle" wrap="wrap">
            <div
              style={{
                flex: 1,
                minWidth: 200,
                padding: '16px 20px',
                background:
                  'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)',
                borderRadius: 8,
                border: '1px solid #b7eb8f',
              }}
            >
              <Flex vertical gap={4}>
                <Text type="secondary">
                  {translate('Total Credit')}
                </Text>
                <Text
                  strong
                  style={{ fontSize: 20, color: '#52c41a' }}
                >
                  {currency} {formatNumber(totalCredit)}
                </Text>
              </Flex>
            </div>
            <div
              style={{
                flex: 1,
                minWidth: 200,
                padding: '16px 20px',
                background:
                  'linear-gradient(135deg, #fff2f0 0%, #ffccc7 100%)',
                borderRadius: 8,
                border: '1px solid #ffa39e',
              }}
            >
              <Flex vertical gap={4}>
                <Text type="secondary">
                  {translate('Total Debit')}
                </Text>
                <Text
                  strong
                  style={{ fontSize: 20, color: '#ff4d4f' }}
                >
                  {currency} {formatNumber(totalDebit)}
                </Text>
              </Flex>
            </div>
            <div
              style={{
                flex: 1,
                minWidth: 200,
                padding: '16px 20px',
                background:
                  'linear-gradient(135deg, #f0f5ff 0%, #d6e4ff 100%)',
                borderRadius: 8,
                border: '1px solid #adc6ff',
              }}
            >
              <Flex vertical gap={4}>
                <Text type="secondary">
                  {translate('Net Amount')}
                </Text>
                <Text
                  strong
                  style={{
                    fontSize: 20,
                    color:
                      totalCredit - totalDebit >= 0
                        ? '#52c41a'
                        : '#ff4d4f',
                  }}
                >
                  {currency}{' '}
                  {formatNumber(Math.abs(totalCredit - totalDebit))}
                  {totalCredit - totalDebit >= 0 ? ' CR' : ' DR'}
                </Text>
              </Flex>
            </div>
          </Flex>

          <Divider style={{ margin: '8px 0' }} />

          <Table
            columns={columns}
            dataSource={transactions}
            rowKey={(record, index) =>
              record.TransactionID || `txn-${index}`
            }
            size="small"
            scroll={{ x: 900, y: 500 }}
            pagination={{
              defaultPageSize: 20,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} ${translate('of')} ${total} ${translate('transactions')}`,
            }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={translate(
                    'No transactions found for the selected period',
                  )}
                />
              ),
            }}
          />
        </Flex>
      </Spin>
    </Drawer>
  );
};

export default TransactionViewDrawer;
